// Web-Audio based sound effects. Zero external assets.
// Sounds are lazily initialised on first user interaction.

let _ctx = null;
let _enabled = true;
let _voiceId = "girl1";

// Voice presets for the 67 Booth. Each preset tunes SpeechSynthesis pitch and
// rate to sound like a different kid — since real voice availability varies
// wildly across devices, pitch/rate tweaks give us a reliable, cross-browser
// audible difference. `nameHints` is a list of case-insensitive substrings we
// try to match against `speechSynthesis.getVoices()` names to prefer a
// real male/female voice when the OS ships one.
export const VOICE_PRESETS = [
  {
    id: "girl1",
    label: "Girl",
    emoji: "👧",
    pitch: 1.7,
    rate: 1,
    nameHints: ["samantha", "karen", "victoria", "female", "google uk english female", "google us english"],
  },
  {
    id: "girl2",
    label: "Squeaky Girl",
    emoji: "🧚",
    pitch: 2,
    rate: 1.15,
    nameHints: ["kids", "junior", "child", "cellos", "trinoids", "female"],
  },
  {
    id: "boy1",
    label: "Boy",
    emoji: "👦",
    pitch: 1.1,
    rate: 1,
    nameHints: ["daniel", "alex", "fred", "male", "google uk english male"],
  },
  {
    id: "boy2",
    label: "Deep Boy",
    emoji: "🧔",
    pitch: 0.6,
    rate: 0.9,
    nameHints: ["ralph", "bruce", "aaron", "male"],
  },
];

const _findPresetVoice = (preset) => {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  for (const hint of preset.nameHints) {
    const v = voices.find((v) => v.name && v.name.toLowerCase().includes(hint));
    if (v) return v;
  }
  return null;
};

const ensureCtx = () => {
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
};

const envTone = (freq, {
  duration = 0.18,
  type = "sine",
  attack = 0.005,
  release = 0.14,
  gain = 0.18,
  delay = 0,
} = {}) => {
  const ctx = ensureCtx();
  if (!ctx) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
  osc.connect(g).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + attack + release + 0.02);
};

const glide = (from, to, duration, options = {}) => {
  const ctx = ensureCtx();
  if (!ctx) return;
  const t = ctx.currentTime + (options.delay || 0);
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = options.type || "triangle";
  osc.frequency.setValueAtTime(from, t);
  osc.frequency.exponentialRampToValueAtTime(to, t + duration);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(options.gain || 0.2, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + duration + 0.05);
};

export const sfx = {
  set enabled(v) {
    _enabled = !!v;
  },
  get enabled() {
    return _enabled;
  },
  // Pick which voice preset the 67 Booth uses. Persisted by StoreContext.
  setVoice(id) {
    if (typeof id !== "string") return;
    if (VOICE_PRESETS.some((v) => v.id === id)) _voiceId = id;
  },
  getVoice() {
    return _voiceId;
  },
  click() {
    if (!_enabled) return;
    envTone(660, { duration: 0.08, type: "square", release: 0.06, gain: 0.09 });
  },
  pop() {
    if (!_enabled) return;
    glide(280, 720, 0.14, { type: "sine", gain: 0.22 });
  },
  add() {
    if (!_enabled) return;
    envTone(880, { duration: 0.1, type: "triangle", gain: 0.18 });
    envTone(1320, { duration: 0.1, type: "triangle", gain: 0.16, delay: 0.06 });
  },
  fav() {
    if (!_enabled) return;
    glide(520, 940, 0.18, { type: "sine", gain: 0.2 });
  },
  coin() {
    if (!_enabled) return;
    envTone(1200, { duration: 0.08, type: "sine", gain: 0.2 });
    envTone(1800, { duration: 0.09, type: "sine", gain: 0.18, delay: 0.05 });
  },
  success() {
    if (!_enabled) return;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
      envTone(f, { duration: 0.16, type: "triangle", gain: 0.2, delay: i * 0.08 })
    );
  },
  fanfare() {
    if (!_enabled) return;
    const notes = [523.25, 659.25, 783.99, 987.77, 1318.51];
    notes.forEach((f, i) =>
      envTone(f, { duration: 0.2, type: "triangle", gain: 0.22, delay: i * 0.1 })
    );
  },
  wheelTick() {
    if (!_enabled) return;
    envTone(1500, { duration: 0.04, type: "square", release: 0.04, gain: 0.06 });
  },
  lose() {
    if (!_enabled) return;
    glide(400, 180, 0.35, { type: "sawtooth", gain: 0.14 });
  },
  reveal() {
    if (!_enabled) return;
    glide(220, 660, 0.22, { type: "triangle", gain: 0.2 });
  },
  // The viral "SIX ... SEVEN!" playground shout. Accepts an intensity level
  // (1..4) that stretches "sevennn" progressively and, at level 4, layers a
  // second "SIX!" shout in front for the full-tier hype.
  // Uses SpeechSynthesis when available for the recognisable cadence;
  // otherwise falls back to a punchy two-note chime so silent devices still
  // get audio feedback.
  sixSeven(level = 1, voiceOverride) {
    if (!_enabled) return;
    const lv = Math.max(1, Math.min(4, level | 0));
    const preset =
      VOICE_PRESETS.find((v) => v.id === (voiceOverride || _voiceId)) ||
      VOICE_PRESETS[0];
    const canSpeak =
      typeof window !== "undefined" &&
      "speechSynthesis" in window &&
      typeof window.SpeechSynthesisUtterance === "function";
    if (canSpeak) {
      try {
        window.speechSynthesis.cancel();
        // Per-level cadence knobs: rate lower = slower/longer, extra n's stretch the vowel.
        const cfg = {
          1: { sixRate: 1.05, sevenRate: 0.72, ns: 3, double: false },
          2: { sixRate: 0.95, sevenRate: 0.55, ns: 6, double: false },
          3: { sixRate: 0.85, sevenRate: 0.42, ns: 10, double: false },
          4: { sixRate: 0.85, sevenRate: 0.35, ns: 14, double: true },
        }[lv];
        const chosenVoice = _findPresetVoice(preset);
        const applyPreset = (u, baseRate) => {
          u.rate = baseRate * preset.rate;
          u.pitch = preset.pitch;
          u.volume = 1;
          if (chosenVoice) u.voice = chosenVoice;
        };
        const speakSix = (delay = 0) =>
          setTimeout(() => {
            const u = new window.SpeechSynthesisUtterance("six");
            applyPreset(u, cfg.sixRate);
            window.speechSynthesis.speak(u);
          }, delay);
        speakSix(0);
        if (cfg.double) speakSix(320);
        const sevenDelay = cfg.double ? 640 : 220;
        setTimeout(() => {
          const seven = new window.SpeechSynthesisUtterance(
            "seve" + "n".repeat(cfg.ns) + "!"
          );
          applyPreset(seven, cfg.sevenRate);
          window.speechSynthesis.speak(seven);
        }, sevenDelay);
        return;
      } catch {
        // fall through to chime
      }
    }
    // Fallback: quick two-note "six-seveeen" chime, scaled by intensity.
    const dur = 0.35 + lv * 0.2;
    envTone(660, { duration: 0.14, type: "triangle", gain: 0.22 });
    glide(700, 1050, dur, { type: "triangle", gain: 0.24, delay: 0.22 });
  },
  // Say just one of the two numbers on demand — cheap standalone shouts.
  // Level 1..4 stretches the vowel the same way sixSeven() does.
  sayNumber(word, level = 1, voiceOverride) {
    if (!_enabled) return;
    const lv = Math.max(1, Math.min(4, level | 0));
    const preset =
      VOICE_PRESETS.find((v) => v.id === (voiceOverride || _voiceId)) ||
      VOICE_PRESETS[0];
    const canSpeak =
      typeof window !== "undefined" &&
      "speechSynthesis" in window &&
      typeof window.SpeechSynthesisUtterance === "function";
    if (canSpeak) {
      try {
        window.speechSynthesis.cancel();
        const rateBy = { 1: 1.05, 2: 0.9, 3: 0.75, 4: 0.55 }[lv];
        const stretch = { 1: 0, 2: 2, 3: 5, 4: 10 }[lv];
        // "six" doesn't stretch its ending nicely — repeat the "iii" instead.
        const spoken =
          word === "six"
            ? "s" + "i".repeat(1 + stretch) + "x"
            : "seve" + "n".repeat(1 + stretch) + "!";
        const u = new window.SpeechSynthesisUtterance(spoken);
        u.rate = rateBy * preset.rate;
        u.pitch = preset.pitch + (word === "seven" ? 0.1 : 0);
        u.volume = 1;
        const chosenVoice = _findPresetVoice(preset);
        if (chosenVoice) u.voice = chosenVoice;
        window.speechSynthesis.speak(u);
        return;
      } catch {
        // fall through to chime
      }
    }
    // Fallback chime — a single note whose pitch matches the word.
    const freq = word === "six" ? 660 : 990;
    envTone(freq, { duration: 0.18 + 0.05 * lv, type: "triangle", gain: 0.22 });
  },
};

export default sfx;
